import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '../stores';
import {
  getItineraries,
  setItineraryDates,
  archiveItinerary,
  unarchiveItinerary,
  softDeleteItinerary,
  restoreItinerary,
  hardDeleteItinerary,
  convertToRoutine,
  getArchivedItineraries,
  getRecentlyDeletedItineraries,
} from '../services';
import { ItineraryListItem } from '../types';
import { usePlanChat } from '../hooks/usePlanChat';
import PlanChatOverlay from '../components/chat/PlanChatOverlay';
import ShareSheet from '../components/plans/ShareSheet';
import SavedTripsSection from '../components/plans/SavedTripsSection';
import UndoToast from '../components/common/UndoToast';
import { BottomNav } from '../components/navigation';
import { newUuid } from '../utils/uuid';
import { restoreNotice } from '../utils/publishOutcome';
import './PlanTab.css';

type LifecycleState = 'in_progress' | 'upcoming' | 'saved' | 'routine' | 'completed';

interface Section {
  key: LifecycleState;
  label: string;
  items: ItineraryListItem[];
  color: string;
  hideIfEmpty?: boolean;
}

const PlanTab: React.FC = () => {
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const [itineraries, setItineraries] = useState<ItineraryListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [editingDraftId, setEditingDraftId] = useState<number | null>(null);
  const planChat = usePlanChat(editingDraftId, editingDraftId ? 'plan_edit' : 'plan_create');
  const [isPlanChatOpen, setIsPlanChatOpen] = useState(false);
  const [emotionalFilter, setEmotionalFilter] = useState<'all' | 'favorites' | 'from_friends' | 'tried_and_loved'>('all');
  const [shareTarget, setShareTarget] = useState<ItineraryListItem | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => new Set(['completed']));
  const [setDatesTarget, setSetDatesTarget] = useState<ItineraryListItem | null>(null);
  const [dismissedPrompts, setDismissedPrompts] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem('vizday_share_prompts_dismissed') || '{}'); } catch { return {}; }
  });
  const dismissPrompt = (key: string) => {
    const next = { ...dismissedPrompts, [key]: true };
    setDismissedPrompts(next);
    try { localStorage.setItem('vizday_share_prompts_dismissed', JSON.stringify(next)); } catch {}
  };

  // Multi-select
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const toggleSelect = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected(new Set());
  };

  const handleBatchArchive = async () => {
    const ids = Array.from(selected);
    setItineraries(prev => prev.filter(i => !ids.includes(i.id)));
    exitSelectMode();
    try {
      await Promise.all(ids.map(id => archiveItinerary(id)));
      refreshCounts();
      setToast({
        message: `${ids.length} plan${ids.length > 1 ? 's' : ''} archived.`,
        undoFn: async () => {
          await Promise.all(ids.map(id => unarchiveItinerary(id)));
          reload();
        },
      });
    } catch { reload(); }
  };

  const handleBatchDelete = async () => {
    const ids = Array.from(selected);
    setItineraries(prev => prev.filter(i => !ids.includes(i.id)));
    exitSelectMode();
    startNoticeOp();
    try {
      await Promise.all(ids.map(id => softDeleteItinerary(id)));
      refreshCounts();
      setToast({
        message: `${ids.length} plan${ids.length > 1 ? 's' : ''} deleted.`,
        undoFn: async () => {
          const op = startNoticeOp();
          // Each restore settles on its own: one failure (a plan deleted
          // forever in another tab) must not hide a restored plan's pending
          // publish, nor skip the refresh the successful restores need.
          const outcomes = await Promise.allSettled(ids.map(id => restoreItinerary(id)));
          const restored = outcomes.flatMap(o => (o.status === 'fulfilled' ? [o.value] : []));
          // One line for the batch: the first restored plan whose publish is pending.
          setNoticeIfCurrent(op, restored.map(r => restoreNotice(r?.publish)).find(n => n !== null) ?? null);
          if (restored.length > 0) {
            reload();
            return;
          }
          // Nothing restored: the old handling, unchanged — the rejection
          // propagates out of the Undo exactly as `Promise.all`'s did.
          const failed = outcomes.find((o): o is PromiseRejectedResult => o.status === 'rejected');
          if (failed) throw failed.reason;
        },
      });
    } catch { reload(); }
  };

  // Lifecycle: kebab, toast, archive/deleted views
  const [kebabFor, setKebabFor] = useState<number | null>(null);
  const [toast, setToast] = useState<{ message: string; undoFn: () => void } | null>(null);
  const [view, setView] = useState<'main' | 'archive' | 'recently_deleted'>('main');
  // The restore notice (private provenance, decision 10): set from the
  // response at every restore site, cleared on the next delete, restore or
  // view change. Every one of those starts a new notice operation by bumping
  // a MONOTONIC generation, and a restore's completion writes the notice only
  // while its own generation is still current — so an older response can
  // neither erase a newer restore's notice nor bring one back after Back (an
  // equality guard on the plan or the view could not tell A→B→A apart). The
  // ref is only ever incremented, never reset in an effect cleanup, so it
  // behaves the same under StrictMode's double-invoked effects.
  const [restoreNoticeText, setRestoreNoticeText] = useState<string | null>(null);
  const noticeGeneration = useRef(0);
  const startNoticeOp = () => {
    noticeGeneration.current += 1;
    setRestoreNoticeText(null);
    return noticeGeneration.current;
  };
  const setNoticeIfCurrent = (op: number, text: string | null) => {
    if (noticeGeneration.current === op) setRestoreNoticeText(text);
  };
  useEffect(() => {
    noticeGeneration.current += 1;
    setRestoreNoticeText(null);
  }, [view]);
  const [archivedPlans, setArchivedPlans] = useState<ItineraryListItem[]>([]);
  const [deletedPlans, setDeletedPlans] = useState<ItineraryListItem[]>([]);
  const [archivedCount, setArchivedCount] = useState(0);
  const [deletedCount, setDeletedCount] = useState(0);
  const [kebabOpensUp, setKebabOpensUp] = useState(false);
  const kebabRef = useRef<HTMLDivElement>(null);

  const toggleSection = (key: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const refreshCounts = useCallback(() => {
    getArchivedItineraries().then(a => setArchivedCount(a.length)).catch(() => {});
    getRecentlyDeletedItineraries().then(d => setDeletedCount(d.length)).catch(() => {});
  }, []);

  const reload = () => {
    getItineraries()
      .then(data => setItineraries(Array.isArray(data) ? data : []))
      .catch(() => setItineraries([]));
    refreshCounts();
  };

  // Close kebab on outside click
  useEffect(() => {
    if (kebabFor === null) return;
    const handler = (e: MouseEvent) => {
      if (kebabRef.current && !kebabRef.current.contains(e.target as Node)) {
        setKebabFor(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [kebabFor]);

  // Keep the popup clear of the fixed bottom navigation (or the viewport edge
  // when the navigation is absent). Re-measure during scrolling because the
  // open card can move while the fixed navigation stays put.
  useLayoutEffect(() => {
    if (kebabFor === null) {
      setKebabOpensUp(false);
      return;
    }

    const wrapper = kebabRef.current;
    const trigger = wrapper?.querySelector<HTMLElement>('.plan-tab-kebab-btn');
    const menu = wrapper?.querySelector<HTMLElement>('.plan-tab-kebab-menu');
    if (!trigger || !menu) return;

    const updatePlacement = () => {
      const navigation = document.querySelector<HTMLElement>('.bottom-nav');
      const lowerBoundary = navigation?.getBoundingClientRect().top || window.innerHeight;
      const spaceBelow = lowerBoundary - trigger.getBoundingClientRect().bottom;
      setKebabOpensUp(spaceBelow < menu.getBoundingClientRect().height + 8);
    };

    updatePlacement();
    window.addEventListener('resize', updatePlacement);
    window.addEventListener('scroll', updatePlacement, true);
    return () => {
      window.removeEventListener('resize', updatePlacement);
      window.removeEventListener('scroll', updatePlacement, true);
    };
  }, [kebabFor]);

  useEffect(() => {
    if (!user) return;
    setIsLoading(true);
    getItineraries()
      .then(data => setItineraries(Array.isArray(data) ? data : []))
      .catch(() => setItineraries([]))
      .finally(() => setIsLoading(false));
    refreshCounts();
  }, [user, refreshCounts]);

  // Group by lifecycle_state with per-section sorting (must be before early return)
  const sections: Section[] = useMemo(() => {
    // Apply emotional filter first
    let filtered = itineraries;
    if (emotionalFilter === 'favorites') {
      filtered = itineraries.filter(i => (i.favorite_count || 0) > 0);
    } else if (emotionalFilter === 'from_friends') {
      filtered = itineraries.filter(i => !!i.source_plan);
    } else if (emotionalFilter === 'tried_and_loved') {
      filtered = itineraries.filter(i =>
        i.lifecycle_state === 'completed' && i.item_count > 0 && ((i.favorite_count || 0) / i.item_count) > 0.5
      );
    }

    const byState = (state: LifecycleState) =>
      filtered.filter(i => i.lifecycle_state === state);

    // Upcoming: start_date ascending (soonest first), ties on id. The tie-break
    // matters because the server's next-trip selector breaks ties the same way
    // (planner/next_trip.py) — without it, equal start dates ordered by whatever
    // the API returned, so the first row here could be a different trip than the
    // one Now calls next.
    const upcoming = byState('upcoming').sort((a, b) => {
      const da = a.start_date || '';
      const db = b.start_date || '';
      return da.localeCompare(db) || a.id - b.id;
    });

    // Saved: updated_at descending (most recently edited first)
    const saved = byState('saved').sort((a, b) => {
      const da = a.updated_at || '';
      const db = b.updated_at || '';
      return db.localeCompare(da);
    });

    // Routines: updated_at descending
    const routines = byState('routine').sort((a, b) => {
      const da = a.updated_at || '';
      const db = b.updated_at || '';
      return db.localeCompare(da);
    });

    // Completed: end_date descending (most recently completed first)
    const completed = byState('completed').sort((a, b) => {
      const da = a.end_date || a.start_date || '';
      const db = b.end_date || b.start_date || '';
      return db.localeCompare(da);
    });

    return [
      { key: 'in_progress' as const, label: 'In progress', items: byState('in_progress'), color: 'var(--accent-primary)', hideIfEmpty: true },
      { key: 'upcoming' as const, label: 'Upcoming', items: upcoming, color: 'var(--color-sage)' },
      { key: 'saved' as const, label: 'Saved', items: saved, color: 'var(--text-secondary)' },
      { key: 'routine' as const, label: 'Routines', items: routines, color: 'var(--color-sage)', hideIfEmpty: true },
      { key: 'completed' as const, label: 'Completed', items: completed, color: 'var(--text-secondary)' },
    ];
  }, [itineraries, emotionalFilter]);

  if (!user) {
    return (
      <div className="plan-tab">
        <div className="plan-tab-unauthed">
          <h2 className="plan-tab-unauthed-title">Your plans live here</h2>
          <p className="plan-tab-unauthed-body">
            Sign up to save plans, browse from the community, and track your adventures.
          </p>
          <button className="plan-tab-unauthed-cta" onClick={() => navigate('/login')}>
            Sign up — it's free
          </button>
        </div>
        <BottomNav activeItem="plan" />
      </div>
    );
  }

  const daysUntil = (dateStr?: string | null) => {
    if (!dateStr) return null;
    const d = new Date(dateStr + 'T00:00:00');
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return Math.max(0, Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
  };

  const durationDays = (p: ItineraryListItem) => {
    if (!p.start_date || !p.end_date) return null;
    return Math.max(1, Math.round((new Date(p.end_date + 'T00:00:00').getTime() - new Date(p.start_date + 'T00:00:00').getTime()) / 86400000) + 1);
  };

  const renderBadge = (sectionKey: string, plan: ItineraryListItem) => {
    switch (sectionKey) {
      case 'in_progress':
        return <span className="plan-tab-badge plan-tab-badge--now">NOW</span>;
      case 'upcoming': {
        const d = daysUntil(plan.start_date);
        return <span className="plan-tab-badge plan-tab-badge--upcoming">{d != null ? `In ${d}d` : 'Soon'}</span>;
      }
      case 'saved':
        return <span className="plan-tab-badge plan-tab-badge--saved">Saved</span>;
      case 'routine':
        return <span className="plan-tab-badge plan-tab-badge--routine">Weekly</span>;
      default:
        return null;
    }
  };

  const handleSetDates = async (startDate: string, endDate?: string) => {
    if (!setDatesTarget) return;
    try {
      await setItineraryDates(
        setDatesTarget.id, startDate, endDate,
        setDatesTarget.content_revision ?? 0,
      );
      setSetDatesTarget(null);
      reload();
    } catch {
      setSetDatesTarget(null);
    }
  };

  const handleArchive = async (plan: ItineraryListItem) => {
    setKebabFor(null);
    // Optimistic remove
    setItineraries(prev => prev.filter(i => i.id !== plan.id));
    try {
      await archiveItinerary(plan.id);
      refreshCounts();
      setToast({
        message: 'Plan archived.',
        undoFn: async () => {
          await unarchiveItinerary(plan.id);
          reload();
        },
      });
    } catch {
      reload(); // revert on error
    }
  };

  const handleDelete = async (plan: ItineraryListItem) => {
    setKebabFor(null);
    setItineraries(prev => prev.filter(i => i.id !== plan.id));
    startNoticeOp();
    try {
      await softDeleteItinerary(plan.id);
      refreshCounts();
      setToast({
        message: 'Plan deleted.',
        undoFn: async () => {
          const op = startNoticeOp();
          const restored = await restoreItinerary(plan.id);
          setNoticeIfCurrent(op, restoreNotice(restored?.publish));
          reload();
        },
      });
    } catch {
      reload();
    }
  };

  const handleConvertToRoutine = async (plan: ItineraryListItem) => {
    if (
      (plan.start_date || plan.end_date)
      && !window.confirm('Move this plan to Routines? This removes its dates.')
    ) return;

    setKebabFor(null);
    try {
      await convertToRoutine(plan.id, plan.content_revision ?? 0);
      reload();
    } catch {}
  };

  const handleUnarchive = async (plan: ItineraryListItem) => {
    setArchivedPlans(prev => prev.filter(i => i.id !== plan.id));
    try {
      await unarchiveItinerary(plan.id);
      reload();
      // Refresh archive list
      getArchivedItineraries().then(setArchivedPlans).catch(() => {});
    } catch {
      getArchivedItineraries().then(setArchivedPlans).catch(() => {});
    }
  };

  const handleRestore = async (plan: ItineraryListItem) => {
    setDeletedPlans(prev => prev.filter(i => i.id !== plan.id));
    const op = startNoticeOp();
    try {
      const restored = await restoreItinerary(plan.id);
      setNoticeIfCurrent(op, restoreNotice(restored?.publish));
      reload();
      getRecentlyDeletedItineraries().then(setDeletedPlans).catch(() => {});
    } catch {
      getRecentlyDeletedItineraries().then(setDeletedPlans).catch(() => {});
    }
  };

  const handleHardDelete = async (plan: ItineraryListItem) => {
    setDeletedPlans(prev => prev.filter(i => i.id !== plan.id));
    startNoticeOp();
    try {
      await hardDeleteItinerary(plan.id);
      refreshCounts();
    } catch {
      getRecentlyDeletedItineraries().then(setDeletedPlans).catch(() => {});
    }
  };

  const openArchiveView = () => {
    exitSelectMode();
    getArchivedItineraries().then(setArchivedPlans).catch(() => setArchivedPlans([]));
    setView('archive');
  };

  const openDeletedView = () => {
    exitSelectMode();
    getRecentlyDeletedItineraries().then(setDeletedPlans).catch(() => setDeletedPlans([]));
    setView('recently_deleted');
  };

  const formatRelativeDate = (dateStr?: string | null) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const daysLeft = (deletedAt?: string | null) => {
    if (!deletedAt) return 30;
    const deleted = new Date(deletedAt);
    const expiry = new Date(deleted.getTime() + 30 * 86400000);
    const now = new Date();
    return Math.max(0, Math.ceil((expiry.getTime() - now.getTime()) / 86400000));
  };

  // One line, rendered at the top of the main and the Recently deleted views.
  const restoreNoticeLine = restoreNoticeText ? (
    <p role="status" className="plan-tab-restore-notice">{restoreNoticeText}</p>
  ) : null;

  // Archive view
  if (view === 'archive') {
    return (
      <div className="plan-tab">
        <div className="plan-tab-header">
          <div className="plan-tab-subview-header">
            <button className="plan-tab-subview-back" aria-label="Back" onClick={() => { setView('main'); reload(); }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
            <h1 className="plan-tab-subview-title">Archive</h1>
          </div>
        </div>
        <div className="plan-tab-content">
          <p className="plan-tab-subview-subtitle">Plans you're keeping but not actively using. Unarchive any time.</p>
          {archivedPlans.length === 0 && (
            <p className="plan-tab-subview-empty">Nothing archived yet.</p>
          )}
          {archivedPlans.map(p => (
            <div key={p.id} className="plan-tab-subview-card">
              <div className="plan-tab-subview-card-accent" />
              <div className="plan-tab-subview-card-body">
                <div className="plan-tab-subview-card-title">{p.title}</div>
                <div className="plan-tab-subview-card-meta">Archived {formatRelativeDate(p.archived_at)}</div>
                <div className="plan-tab-subview-actions">
                  <button className="plan-tab-subview-action" onClick={() => handleUnarchive(p)}>Unarchive</button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <BottomNav activeItem="plan" />
      </div>
    );
  }

  // Recently deleted view
  if (view === 'recently_deleted') {
    return (
      <div className="plan-tab">
        <div className="plan-tab-header">
          <div className="plan-tab-subview-header">
            <button className="plan-tab-subview-back" aria-label="Back" onClick={() => { setView('main'); reload(); }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
            <h1 className="plan-tab-subview-title">Recently deleted</h1>
          </div>
        </div>
        <div className="plan-tab-content">
          {restoreNoticeLine}
          <p className="plan-tab-subview-subtitle">Plans here are deleted permanently after 30 days.</p>
          {deletedPlans.length === 0 && (
            <p className="plan-tab-subview-empty">Nothing here.</p>
          )}
          {deletedPlans.map(p => (
            <div key={p.id} className="plan-tab-subview-card plan-tab-subview-card--deleted">
              <div className="plan-tab-subview-card-accent" />
              <div className="plan-tab-subview-card-body">
                <div className="plan-tab-subview-card-title">{p.title}</div>
                <div className="plan-tab-subview-card-meta">
                  Deleted {formatRelativeDate(p.deleted_at)} &middot; {daysLeft(p.deleted_at)} days left
                </div>
                <div className="plan-tab-subview-actions">
                  <button className="plan-tab-subview-action" onClick={() => handleRestore(p)}>Restore</button>
                  <button className="plan-tab-subview-action plan-tab-subview-action--danger" onClick={() => handleHardDelete(p)}>Delete forever</button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <BottomNav activeItem="plan" />
      </div>
    );
  }

  return (
    <div className="plan-tab">
      <div className="plan-tab-content">
        {restoreNoticeLine}
        {/* New Plan CTA or Selection bar */}
        {!selectMode ? (
          <button
            className="plan-tab-new-btn"
            onClick={() => {
              setEditingDraftId(null);
              planChat.reset({
                intent: 'new_plan', entryId: newUuid(),
                query: 'Help me plan something',
              });
              setIsPlanChatOpen(true);
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
              <path d="M12 5v14M5 12h14"/>
            </svg>
            New Plan with AI
          </button>
        ) : (
          <div className="plan-tab-select-bar">
            <span className="plan-tab-select-count">{selected.size} selected</span>
            <div className="plan-tab-select-actions">
              <button className="plan-tab-select-btn plan-tab-select-btn--archive" onClick={handleBatchArchive} disabled={selected.size === 0}>Archive</button>
              <button className="plan-tab-select-btn plan-tab-select-btn--delete" onClick={handleBatchDelete} disabled={selected.size === 0}>Delete</button>
              <button className="plan-tab-select-btn plan-tab-select-btn--cancel" onClick={exitSelectMode}>Cancel</button>
            </div>
          </div>
        )}

        {/* Emotional filter pills */}
        {itineraries.length > 0 && (
          <div className="plan-tab-filter-pills">
            {([
              { key: 'all', label: 'All' },
              { key: 'favorites', label: 'Favorites' },
              { key: 'from_friends', label: 'From others' },
              { key: 'tried_and_loved', label: 'Tried & loved' },
            ] as const).map(f => (
              <button
                key={f.key}
                className={`plan-tab-filter-pill ${emotionalFilter === f.key ? 'plan-tab-filter-pill--active' : ''}`}
                onClick={() => setEmotionalFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        {/* Guides saved without dates (spec §22.3) — above the lifecycle
            sections, because a save is an intention waiting on a decision and
            the decision is the next thing to make. */}
        <SavedTripsSection />

        {/* Lifecycle sections */}
        {sections.map(section => {
          if (section.hideIfEmpty && section.items.length === 0) return null;
          const isCollapsed = collapsedSections.has(section.key);

          return (
            <div key={section.key} className="plan-tab-section">
              <button
                className="plan-tab-section-header"
                onClick={() => toggleSection(section.key)}
              >
                <div className="plan-tab-section-label-row">
                  <span className="plan-tab-section-label" style={{ color: section.color }}>
                    {section.label.toUpperCase()}
                  </span>
                  {section.items.length > 0 && (
                    <span className="plan-tab-section-count">{section.items.length}</span>
                  )}
                </div>
                <svg
                  className={`plan-tab-section-chevron ${isCollapsed ? '' : 'plan-tab-section-chevron--open'}`}
                  width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                >
                  <path d="M6 9l6 6 6-6"/>
                </svg>
              </button>

              {!isCollapsed && (
                <>
                  {section.items.length === 0 && (
                    <p className="plan-tab-section-empty">Nothing here yet.</p>
                  )}

                  {section.items.map(p => {
                    const dur = durationDays(p);
                    const isSelected = selected.has(p.id);
                    return (
                      <div
                        key={p.id}
                        className={`plan-tab-card ${section.key === 'completed' ? 'plan-tab-card--completed' : ''} ${isSelected ? 'plan-tab-card--selected' : ''} ${kebabFor === p.id ? 'plan-tab-card--menu-open' : ''}`}
                        onClick={() => {
                          if (selectMode) { toggleSelect(p.id); }
                          else { navigate(`/itinerary/${p.id}`); }
                        }}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          if (!selectMode) { setSelectMode(true); setSelected(new Set([p.id])); }
                        }}
                        onTouchStart={() => {
                          if (selectMode) return;
                          longPressTimer.current = setTimeout(() => {
                            setSelectMode(true);
                            setSelected(new Set([p.id]));
                          }, 500);
                        }}
                        onTouchEnd={() => { if (longPressTimer.current) clearTimeout(longPressTimer.current); }}
                        onTouchMove={() => { if (longPressTimer.current) clearTimeout(longPressTimer.current); }}
                      >
                        {selectMode && (
                          <div className="plan-tab-card-checkbox">
                            <div className={`plan-tab-checkbox ${isSelected ? 'plan-tab-checkbox--checked' : ''}`}>
                              {isSelected && (
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="plan-tab-card-accent" style={{ background: 'var(--color-sage)' }} />
                        <div className="plan-tab-card-body">
                          <div className="plan-tab-card-main">
                            <div className="plan-tab-card-info">
                              <div className="plan-tab-card-title">{p.title}</div>
                              <div className="plan-tab-card-meta">
                                {p.destination || ''}
                                {dur ? ` · ${dur} days` : ''}
                                {p.item_count ? ` · ${p.item_count} activities` : ''}
                                {section.key === 'completed' && p.favorite_count ? ` · ${p.favorite_count} favorites` : ''}
                                {p.source_plan && p.creator_name ? ` · from ${p.creator_name}` : ''}
                              </div>
                              {section.key === 'saved' && p.created_at && (
                                <div className="plan-tab-card-detail">Saved {new Date(p.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                              )}
                              {section.key === 'routine' && p.last_used_at && (
                                <div className="plan-tab-card-detail">Last used {new Date(p.last_used_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                              )}
                            </div>
                            <div className="plan-tab-card-actions">
                              {renderBadge(section.key, p)}
                              {section.key === 'saved' && (
                                <button
                                  className="plan-tab-set-dates-btn"
                                  onClick={(e) => { e.stopPropagation(); setSetDatesTarget(p); }}
                                >
                                  Set dates
                                </button>
                              )}
                              {!selectMode && <div className="plan-tab-kebab-wrapper" ref={kebabFor === p.id ? kebabRef : undefined}>
                                <button
                                  className="plan-tab-kebab-btn"
                                  onClick={(e) => { e.stopPropagation(); setKebabFor(kebabFor === p.id ? null : p.id); }}
                                  aria-label="More actions"
                                  aria-expanded={kebabFor === p.id}
                                >
                                  &#x22EF;
                                </button>
                                {kebabFor === p.id && (
                                  <div className={`plan-tab-kebab-menu ${kebabOpensUp ? 'plan-tab-kebab-menu--up' : ''}`} role="group" aria-label="Plan actions" onClick={(e) => e.stopPropagation()}>
                                    <button className="plan-tab-kebab-item" onClick={() => { setKebabFor(null); navigate(`/plans/${p.id}/similar`); }}>Find similar</button>
                                    {!p.is_routine && (
                                      <button className="plan-tab-kebab-item" onClick={() => handleConvertToRoutine(p)}>Move to Routines</button>
                                    )}
                                    <button className="plan-tab-kebab-item" onClick={() => handleArchive(p)}>Archive</button>
                                    <hr className="plan-tab-kebab-divider" />
                                    <button className="plan-tab-kebab-item plan-tab-kebab-item--danger" onClick={() => handleDelete(p)}>Delete</button>
                                  </div>
                                )}
                              </div>}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Share prompts */}
                  {section.key === 'upcoming' && section.items.length > 0 && !dismissedPrompts['upcoming'] && (
                    <div className="plan-tab-share-prompt">
                      <div className="plan-tab-share-prompt-text">
                        <div className="plan-tab-share-prompt-title">Your plan is ready — share it?</div>
                        <div className="plan-tab-share-prompt-body">Others can make it their own.</div>
                      </div>
                      <button className="plan-tab-share-prompt-btn" onClick={() => setShareTarget(section.items[0] || null)}>Share</button>
                      <button className="plan-tab-share-prompt-dismiss" onClick={() => dismissPrompt('upcoming')}>×</button>
                    </div>
                  )}

                  {section.key === 'completed' && section.items.length > 0 && !dismissedPrompts['completed'] && (
                    <div className="plan-tab-share-prompt plan-tab-share-prompt--completed">
                      <div className="plan-tab-share-prompt-text">
                        <div className="plan-tab-share-prompt-title">Plan complete — share how it went?</div>
                        <div className="plan-tab-share-prompt-body">Your recap helps others plan.</div>
                      </div>
                      <button className="plan-tab-share-prompt-btn plan-tab-share-prompt-btn--sage" onClick={() => setShareTarget(section.items[0] || null)}>Share</button>
                      <button className="plan-tab-share-prompt-dismiss" onClick={() => dismissPrompt('completed')}>×</button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}

        {/* Empty state */}
        {!isLoading && itineraries.length === 0 && (
          <div className="plan-tab-empty">
            <p className="plan-tab-empty-title">No plans yet</p>
            <p className="plan-tab-empty-body">Start planning with AI or save a plan from Explore.</p>
          </div>
        )}

        {/* Footer links */}
        {(archivedCount > 0 || deletedCount > 0) && (
          <div className="plan-tab-footer-links">
            {archivedCount > 0 && (
              <button className="plan-tab-footer-link" onClick={openArchiveView}>
                {'📦 Archive · '}{archivedCount}{' →'}
              </button>
            )}
            {deletedCount > 0 && (
              <button className="plan-tab-footer-link" onClick={openDeletedView}>
                {'🗑️ Recently deleted · '}{deletedCount}{' →'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Set Dates Modal */}
      {setDatesTarget && (
        <div className="plan-tab-dates-overlay" onClick={() => setSetDatesTarget(null)}>
          <div className="plan-tab-dates-sheet" onClick={e => e.stopPropagation()}>
            <div className="plan-tab-dates-handle" />
            <h3 className="plan-tab-dates-title">Set dates for {setDatesTarget.title}</h3>
            <p className="plan-tab-dates-subtitle">{setDatesTarget.destination || 'No destination'}</p>
            <form onSubmit={e => {
              e.preventDefault();
              const form = e.target as HTMLFormElement;
              const start = (form.elements.namedItem('start_date') as HTMLInputElement).value;
              const end = (form.elements.namedItem('end_date') as HTMLInputElement).value;
              if (start) handleSetDates(start, end || undefined);
            }}>
              <label className="plan-tab-dates-label">
                Start date
                <input type="date" name="start_date" required className="plan-tab-dates-input" />
              </label>
              <label className="plan-tab-dates-label">
                End date <span className="plan-tab-dates-optional">(optional)</span>
                <input type="date" name="end_date" className="plan-tab-dates-input" />
              </label>
              <div className="plan-tab-dates-actions">
                <button type="button" className="plan-tab-dates-cancel" onClick={() => setSetDatesTarget(null)}>Cancel</button>
                <button type="submit" className="plan-tab-dates-confirm">Set dates</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <PlanChatOverlay
        chat={planChat}
        isExpanded={isPlanChatOpen}
        onClose={() => setIsPlanChatOpen(false)}
        onNavigate={(path) => navigate(path)}
        chatType={editingDraftId ? 'plan_edit' : 'plan_create'}
      />

      {shareTarget && (
        <ShareSheet
          itineraryId={shareTarget.id}
          title={shareTarget.title}
          destination={shareTarget.destination || ''}
          currentVisibility={shareTarget.visibility || 'private'}
          currentSlug={shareTarget.slug}
          onClose={() => setShareTarget(null)}
        />
      )}

      {toast && (
        <UndoToast
          message={toast.message}
          onUndo={() => { toast.undoFn(); setToast(null); }}
          onDismiss={() => setToast(null)}
        />
      )}

      <BottomNav activeItem="plan" />
    </div>
  );
};

export default PlanTab;
